import { useMemo } from 'react';
import type { ComponentNode } from '@deckspace/types';
import { DATA_BACKED_TYPES, findRootComponent } from '@deckspace/types';
import { getVisualization } from '@deckspace/catalog';
import { getCatalogComponent } from './catalog';
import { deriveTracks } from './layout-template';
import { resolveStyle } from './style-resolver';
import { SurfaceProvider } from './SurfaceContext';
import { MeasuredContainer } from './MeasuredContainer';
import { NodeErrorBoundary } from './NodeErrorBoundary';

interface SurfaceRendererProps {
  components: ComponentNode[];
  workspaceId: string;
  themeKey?: number;
  onComponentPropsChange?: (nodeId: string, props: Record<string, unknown>) => void;
}

function UnknownComponent({ type }: { type: string }) {
  return (
    <div className="border border-dashed border-amber-400 rounded p-2 text-xs text-amber-600">
      Unknown component: {type}
    </div>
  );
}

function DataPlaceholder({ message }: { message: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        color: 'var(--color-muted)',
        fontSize: '0.875rem',
        border: '1px dashed var(--color-border, #e2e8f0)',
        borderRadius: '0.5rem',
        minHeight: '120px',
      }}
    >
      {message}
    </div>
  );
}

function needsMeasured(type: string): boolean {
  return getVisualization(type)?.needsMeasuredDims === true;
}

// Uniform cell class for every wrapped child. The parent container's CSS
// layout (grid track / flex panel) sizes the wrapper; min-w-0/min-h-0
// prevents min-content overflow from blowing out a grid cell.
const CELL_CLASS = 'min-w-0 min-h-0 w-full h-full overflow-hidden';

function NodeRenderer({
  nodeId,
  nodeMap,
  measuredWidth,
  measuredHeight,
  alreadyWrapped,
}: {
  nodeId: string;
  nodeMap: Map<string, ComponentNode>;
  measuredWidth?: number;
  measuredHeight?: number;
  // Set true on the re-entered call inside a MeasuredContainer to break the
  // wrap recursion (this node was just wrapped by its parent or by the root
  // branch; don't wrap it again).
  alreadyWrapped?: boolean;
}) {
  const node = nodeMap.get(nodeId);
  if (!node) return null;

  const Component = getCatalogComponent(node.type);
  if (!Component) {
    return <UnknownComponent type={node.type} />;
  }

  // Graceful degradation for data-backed components missing their dataset
  if (DATA_BACKED_TYPES.has(node.type)) {
    const props = node.props as Record<string, unknown> | undefined;
    if (!props?.sourceDataset) {
      return <DataPlaceholder message="Dataset not found" />;
    }
  }

  // Resolve user-provided style (bg, padding, etc.) into Tailwind classes + inline styles
  const resolved = resolveStyle(node.props?.style as Parameters<typeof resolveStyle>[0]);

  // Build child elements. Wrap each child whose catalog says needsMeasuredDims.
  // The decision is parent-agnostic — same rule applies inside Row, Column,
  // Tabs, Accordion, or no container at all.
  const childElements = node.children?.map((childId) => {
    const child = nodeMap.get(childId);
    if (!child) return null;

    // Layout children (Row, Column, Tabs, Accordion, Divider) manage their
    // own grid/flex layout and don't produce leaf content. No cell chrome
    // for them — they sit directly in their parent's track.
    const def = getVisualization(child.type);
    if (def?.category === 'layout') {
      return <NodeRenderer key={childId} nodeId={childId} nodeMap={nodeMap} />;
    }

    // Needs-measured leaf viz (charts) — MeasuredContainer already carries
    // CELL_CLASS plus a ResizeObserver that feeds pixel dims to the child.
    if (needsMeasured(child.type)) {
      return (
        <MeasuredContainer key={childId} className={CELL_CLASS}>
          {(width, height) => (
            <NodeRenderer
              nodeId={childId}
              nodeMap={nodeMap}
              measuredWidth={width}
              measuredHeight={height}
              alreadyWrapped
            />
          )}
        </MeasuredContainer>
      );
    }

    // Non-layout, non-measured leaf viz — wrap in a plain cell chrome div
    // so the cell is a well-behaved grid item and any inner overflow-auto
    // (e.g. shared/Table.tsx, EditableTable.tsx) is bounded by this clip.
    return (
      <div key={childId} className={CELL_CLASS}>
        <NodeRenderer nodeId={childId} nodeMap={nodeMap} />
      </div>
    );
  });

  // Derive grid template for Row/Column from children + catalog defaults.
  const template =
    node.type === 'Row'
      ? deriveTracks(node, nodeMap, 'col')
      : node.type === 'Column'
        ? deriveTracks(node, nodeMap, 'row')
        : undefined;

  const classParts = [resolved.className, node.props?.className as string | undefined].filter(
    Boolean
  );
  const mergedClassName = classParts.length > 0 ? classParts.join(' ') : undefined;

  const componentProps: Record<string, unknown> = {
    ...(node.props ?? {}),
    nodeId: node.id,
    className: mergedClassName,
    style: Object.keys(resolved.style).length > 0 ? resolved.style : undefined,
  };
  if (template !== undefined) componentProps.template = template;
  if (measuredWidth !== undefined) componentProps.width = measuredWidth;
  if (measuredHeight !== undefined) componentProps.height = measuredHeight;

  // Needs-measured root: wrap in MeasuredContainer with viewport sizing.
  // Covers the "single chart as entire surface" case. alreadyWrapped breaks
  // the recursion on the re-entered call inside the MeasuredContainer.
  if (!alreadyWrapped && needsMeasured(node.type) && measuredWidth === undefined) {
    return (
      <NodeErrorBoundary nodeId={node.id} nodeType={node.type}>
        <MeasuredContainer className={mergedClassName ?? 'w-full'} style={{ minHeight: '100vh' }}>
          {(width, height) => (
            <NodeRenderer
              nodeId={nodeId}
              nodeMap={nodeMap}
              measuredWidth={width}
              measuredHeight={height}
              alreadyWrapped
            />
          )}
        </MeasuredContainer>
      </NodeErrorBoundary>
    );
  }

  return (
    <NodeErrorBoundary nodeId={node.id} nodeType={node.type}>
      <Component {...componentProps}>{childElements}</Component>
    </NodeErrorBoundary>
  );
}

export function SurfaceRenderer({
  components,
  workspaceId,
  themeKey,
  onComponentPropsChange,
}: SurfaceRendererProps) {
  const nodeMap = useMemo(() => {
    const map = new Map<string, ComponentNode>();
    for (const node of components) {
      map.set(node.id, node);
    }
    return map;
  }, [components]);

  const contextValue = useMemo(
    () => ({ workspaceId, themeKey, onComponentPropsChange }),
    [workspaceId, themeKey, onComponentPropsChange]
  );

  const rootNode = findRootComponent(components);
  if (!rootNode) {
    return (
      <div className="flex items-center justify-center p-8 text-sm text-red-500">
        No root component found in composition tree.
      </div>
    );
  }

  // Outer wrapper supplies a definite height context so layout containers
  // with `minHeight: 100%` (ColumnView, RowView) actually resolve to a
  // pixel value. `min-height: 100vh` (Tailwind `min-h-screen`) only sets a
  // floor — the wrapper's `height` property stays `auto`, so percentage
  // heights on children collapse to 0. `h-full` makes the wrapper take
  // 100% of its parent's height (an `overflow-auto` flex item with a
  // definite used-height), which then propagates to the surface root.
  return (
    <SurfaceProvider value={contextValue}>
      <div className="w-full h-full">
        <NodeRenderer nodeId={rootNode.id} nodeMap={nodeMap} />
      </div>
    </SurfaceProvider>
  );
}
